/**
 * Turning an order into the things a Parcel label has to say.
 *
 * WHAT A LABEL IS FOR
 *
 * Two different jobs are printed on the same piece of paper, and keeping them
 * straight is what makes the label useful:
 *
 *   IDENTITY - the code and its QR. Machine-readable for custody scans,
 *   human-readable so a person can read it down a phone line, and the same
 *   string the shopper sees on their order, so both ends of a question about a
 *   parcel can name the same thing.
 *
 *   DELIVERY - who it is going to, where, and where it goes back to. This is
 *   what makes the parcel deliverable AS AN OBJECT. Until now the label carried
 *   only identity, which meant a parcel separated from the Rider's phone was an
 *   anonymous bag: the app knew where it was going and nothing on the parcel
 *   did. Every courier label in the world carries the address for exactly this
 *   reason - phones die, apps break, parcels get put down in the wrong pile.
 *
 * THE SORT TOKEN
 *
 * Couriers print a big short code so a Rider can find one parcel in a stack
 * without reading twenty-nine characters off each. Theirs is assigned at a
 * sorting hub; ours has no hub and the label is printed before any trip exists,
 * so the stop number is not knowable yet. The destination city plus the tail of
 * the parcel code is what IS knowable at print time, and it sorts a rider's bag
 * just as well.
 */

/** The last run of characters in the code - short, printed large, easy to shout. */
export function sortToken(labelCode) {
  const code = String(labelCode || '').trim();
  if (!code) return null;
  const tail = code.replace(/[^A-Za-z0-9]/g, '').slice(-4).toUpperCase();
  return tail || null;
}

/** The address block, in the order a person reads it aloud. */
export function shipToLines(shipTo) {
  if (!shipTo) return [];
  const locality = [shipTo.barangay, shipTo.city, shipTo.province]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');

  return [
    shipTo.streetAddress,
    locality,
    shipTo.postalCode ? String(shipTo.postalCode) : null,
    // A landmark is often the only thing that finds a house here, so it is part
    // of the address rather than a note hidden somewhere else.
    shipTo.landmark ? `Landmark: ${shipTo.landmark}` : null,
  ].map((line) => String(line || '').trim()).filter(Boolean);
}

/**
 * Everything the printed label needs, or a reason it cannot be printed.
 *
 * A label missing its destination is worse than no label: it looks complete and
 * is undeliverable. So a missing address is reported rather than printed around.
 */
export function buildParcelLabel(order) {
  const label = order?.parcelLabel;
  if (!label?.code) return { printable: false, reason: 'This Parcel has no label code yet.' };

  const shipTo = order.shipTo || {};
  const lines = shipToLines(shipTo);
  const recipient = String(shipTo.name || '').trim();

  if (!recipient || lines.length === 0) {
    return {
      printable: false,
      reason: 'This order has no delivery name and address, so a label would be undeliverable if it left the store.',
    };
  }

  return {
    printable: true,
    code: label.code,
    qrPayload: label.qrPayload,
    sortToken: sortToken(label.code),
    orderReference: order.orderReference,
    shipTo: {
      name: recipient,
      phone: String(shipTo.phone || '').trim() || null,
      lines,
      city: String(shipTo.city || '').trim() || null,
    },
    returnTo: {
      businessName: String(order.returnTo?.businessName || '').trim() || null,
      address: String(order.returnTo?.address || '').trim() || null,
    },
  };
}
